export const isValidFinnishSocialSecurityNumber = (
    socialSecurityNumber: string,
): boolean => {
    // Match the Finnish SSN pattern: DDMMYY-XXXC (1900) or DDMMYYAXXXC (2000)
    const format: RegExp =
        /^(\d{2})(\d{2})(\d{2})([-A])(\d{3})([0-9A-FHJ-NPR-Y])$/;

    // Check if the format matches the required format
    if (!format.test(socialSecurityNumber)) {
        return false;
    }

    // Extract date components
    const [, day, month, year, separator, individual, checksumChar] =
        socialSecurityNumber.match(format)!;

    // Determine century based on separator character (only "-" for 1900s and "A" for 2000s)
    let century;
    if (separator === "-") {
        century = 1900;
    } else if (separator === "A") {
        century = 2000;
    } else {
        return false; // Invalid century marker, skip unsupported characters
    }

    const fullYear = century + parseInt(year, 10);

    // Date validation for month and day, accounting for leap years
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    const date = new Date(fullYear, monthNum - 1, dayNum);

    const isValidDate =
        date.getFullYear() === fullYear &&
        date.getMonth() === monthNum - 1 &&
        date.getDate() === dayNum;

    if (!isValidDate) {
        return false;
    }

    // Finnish SSN checksum calculation based on document specification
    const finnishChecksum = (value: string): boolean => {
        const checksumChars = "0123456789ABCDEFHJKLMNPRSTUVWXY";
        const checkValue = parseInt(value, 10) % 31;
        return checksumChars[checkValue] === checksumChar;
    };

    // Form the full numeric value for checksum calculation
    const numericValue = `${day}${month}${year}${individual}`;

    // Verify checksum for the SSN
    return finnishChecksum(numericValue);
};

export const isValidSwedishSocialSecurityNumber = (
    socialSecurityNumber: string,
): boolean => {
    const isValidInput =
        typeof socialSecurityNumber === "string" &&
        socialSecurityNumber.length === 12;

    if (!isValidInput) {
        return false;
    }

    // Match the Swedish SSN pattern: YYYYMMDDBBBC (12 digits)
    const format: RegExp = /^(19|20)\d{10}$/;
    if (!format.test(socialSecurityNumber)) {
        return false;
    }

    // Extract year, month, and day
    const year: number = parseInt(socialSecurityNumber.slice(0, 4), 10);
    const month: number = parseInt(socialSecurityNumber.slice(4, 6), 10);
    const day: number = parseInt(socialSecurityNumber.slice(6, 8), 10);

    // Check if the date is valid
    const date = new Date(year, month - 1, day);
    const isValidDate =
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day;

    if (!isValidDate) {
        return false;
    }

    // Luhn checksum calculation on the last 10 digits (YYMMDDBBBC)
    const luhnCheck = (value: string): boolean => {
        const digits: number[] = value.split("").map(Number);
        const checksum: number = digits.reduce((sum, digit, index) => {
            const temp: number = digit * (index % 2 === 0 ? 2 : 1);
            return sum + (temp > 9 ? temp - 9 : temp);
        }, 0);
        return checksum % 10 === 0;
    };

    // Apply Luhn check to last 10 digits only
    const isValidSSN = luhnCheck(socialSecurityNumber.slice(2));

    return isValidSSN;
};
